import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import { Container } from "reactstrap";
import { get, deleteRequest } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import trashIcon from "../../../assets/images/support/trash-icon.svg";

/* -------------------------------------------------------------------------
 * AdminRestaurantConnectAccountsPage — Restaurant Connect Accounts management
 * ---------------------------------------------------------------------- */

const columns = (onDelete) => [
  { key: "id", label: "ID", width: "0.6fr" },
  { key: "restaurant_name", label: "Restaurant Name", width: "1.5fr" },
  { key: "restaurant_email", label: "Restaurant Email", width: "1.5fr" },
  { key: "stripe_account_id", label: "Stripe Account ID", width: "1.8fr" },
  { key: "onboarding_status", label: "Onboarding Status", width: "1.2fr" },
  { key: "payouts_enabled", label: "Payouts Enabled", width: "0.8fr" },
  { key: "charges_enabled", label: "Charges Enabled", width: "0.8fr" },
  { key: "created_at", label: "Created At", width: "1.2fr" },
  {
    key: "actions",
    label: "",
    width: "80px",
    align: "center",
    render: (row) => (
      <button
        type="button"
        onClick={() => onDelete && onDelete(row.id)}
        className="delete-btn"
        aria-label={`Delete account ${row.id}`}
        style={{
          padding: "8px",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={trashIcon} alt="Delete" style={{ width: "20px", height: "20px" }} />
      </button>
    ),
  },
];

const AdminRestaurantConnectAccountsPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Restaurant Connect Accounts | Omaha Food Lovers";
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [vendorListingId, setVendorListingId] = useState("");
  const [restaurants, setRestaurants] = useState([]);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("page_size", 20);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    if (status) {
      params.append("status", status);
    }

    if (vendorListingId) {
      params.append("vendor_listing_id", vendorListingId);
    }

    return params.toString();
  };

  const fetchAccounts = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminRestaurantsConnectAccounts}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const data = response.data.data;
        const rawAccounts = data.data || [];

        // Transform API data to component format
        const newAccounts = rawAccounts.map(account => ({
          id: account.id,
          restaurant_name: account.vendor_listing?.title || 'Unknown',
          restaurant_email: account.vendor_listing?.email || 'N/A',
          stripe_account_id: account.stripe_account_id || 'N/A',
          onboarding_status: account.onboarding_status || 'Unknown',
          payouts_enabled: account.payouts_enabled ? 'Yes' : 'No',
          charges_enabled: account.charges_enabled ? 'Yes' : 'No',
          created_at: formatDate(account.created_at),
        }));

        if (isLoadMore) {
          setAccounts((prev) => [...prev, ...newAccounts]);
        } else {
          setAccounts(newAccounts);
        }

        setHasMore(newAccounts.length === 20);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch restaurant connect accounts:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchAccounts(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const fetchRestaurants = async () => {
    try {
      const response = await get(`${endpoints.adminAllRestaurants}&page=1&per_page=100`);
      if (response.data?.data) {
        const data = response.data.data;
        const rawRestaurants = data.data || [];

        const restaurantOptions = rawRestaurants.map((r) => ({
          value: r.id,
          label: r.title || r.name || `Restaurant ${r.id}`,
        }));

        setRestaurants(restaurantOptions);
      }
    } catch (error) {
      console.error("Failed to fetch restaurants:", error);
    }
  };

  const handleDeleteClick = (accountId) => {
    setAccountToDelete(accountId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;

    try {
      const deleteEndpoint = endpoints.adminDeleteRestaurantConnectAccount.replace('{{connect_account_id}}', accountToDelete);
      await deleteRequest(deleteEndpoint);
      
      toastr.success("Connect account deleted successfully!");
      setIsDeleteModalOpen(false);
      setAccountToDelete(null);
      
      // Refresh the list
      fetchAccounts(1, false);
    } catch (error) {
      console.error("Failed to delete connect account:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setAccountToDelete(null);
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.adminExport}?type=restaurant_connect_accounts`);
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'restaurant_connect_accounts.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toastr.success("Export successful!");
    } catch (error) {
      console.error("Failed to export data:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    }
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "status",
      type: "select",
      label: "Status",
      value: status,
      onChange: setStatus,
      options: [
        { value: "", label: "All" },
        { value: "completed", label: "Completed" },
        { value: "pending", label: "Pending" },
        { value: "requirements_due", label: "Requirements Due" },
        { value: "submitted", label: "Submitted" },
        { value: "restricted", label: "Restricted" },
        { value: "disabled", label: "Disabled" },
      ],
    },
    {
      key: "restaurant",
      type: "select",
      label: "Restaurant",
      value: vendorListingId,
      onChange: setVendorListingId,
      options: restaurants,
      searchable: true,
    },
  ];

  useEffect(() => {
    fetchRestaurants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAccounts(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, vendorListingId]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Restaurant Connect Accounts</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of restaurant Stripe connect accounts and status.</p>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by restaurant name or email"
            filters={filters}
            showExport
            onExport={handleExport}
          />

        <DataTable
          columns={columns(handleDeleteClick)}
          rows={accounts}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        /></div>
      </Container>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Delete Connect Account"
        message="Are you sure you want to delete this connect account? This action cannot be undone."
        itemName="connect account"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

AdminRestaurantConnectAccountsPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminRestaurantConnectAccountsPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminRestaurantConnectAccountsPage;
